import { IconSearch, IconX } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { PaginationState } from "@tanstack/react-table";
import { useMemo } from "react";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DebouncedInput } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useFilters } from "@/hooks/use-filter";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, sortByToState, stateToSortBy } from "@/lib/table";
import { orpc } from "@/orpc/client";
import { EpisodesFilters } from "@/orpc/router/episodes";
import { columns as episodeColumns } from "@/tables/episodes";

export const Route = createFileRoute("/_app/episodes/")({
	component: Page,
	validateSearch: () => ({}) as EpisodesFilters,
	async loader({ context, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.list.queryOptions({
				input: {},
			}),
		);
		context.queryClient.prefetchQuery(
			orpc.episodes.listStatuses.queryOptions({
				input: {},
				staleTime: Infinity,
			}),
		);
	},
});

function Page() {
	const { filters, resetFilters, setFilters } = useFilters(Route.id);

	const { data: episodeStatuses } = useQuery(
		orpc.episodes.listStatuses.queryOptions({ input: {}, staleTime: Infinity }),
	);

	const { data, isFetching, refetch } = useQuery(
		orpc.episodes.list.queryOptions({
			input: filters,
			placeholderData: keepPreviousData,
		}),
	);

	const paginationState: PaginationState = {
		pageIndex: filters.pageIndex ?? DEFAULT_PAGE_INDEX,
		pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
	};
	const sortingState = sortByToState(filters.sortBy || "ID.desc");
	const columns = useMemo(() => episodeColumns, []);

	const ToolBar = () => (
		<>
			<div className="flex items-center gap-2">
				<div className="relative">
					<DebouncedInput
						value={filters.q}
						onValueChange={(value) => {
							setFilters({ q: value });
						}}
						placeholder="Search Episodes..."
						className="pl-10"
					/>
					<div className="pointer-events-none absolute top-0 left-2 flex h-full items-center justify-center">
						<IconSearch className="h-4" />
					</div>
				</div>
				<Select
					value={filters.StatusID}
					onValueChange={(value) => {
						setFilters({ StatusID: typeof value === "number" ? value : undefined });
					}}
				>
					<SelectTrigger>
						<SelectValue placeholder="Filter by Status">
							{(value) => {
								const status = episodeStatuses?.find((s) => s.ID === value);

								return status ? status.Name : "Filter by Status";
							}}
						</SelectValue>
					</SelectTrigger>
					<SelectContent side="bottom">
						<SelectItem value="">All Statuses</SelectItem>
						{(episodeStatuses ?? []).map((status) => (
							<SelectItem key={status.ID} value={status.ID}>
								{status.Name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{Object.keys(filters).filter((k) => !["pageIndex", "pageSize", "sortBy"].includes(k))
					.length > 0 && (
					<Button variant="outline" onClick={() => resetFilters()}>
						<IconX /> Clear
					</Button>
				)}
			</div>

			{/* <CreateChannelDialog>
				<Button>
					<IconPlus />
					Create a Channel
				</Button>
			</CreateChannelDialog> */}
		</>
	);

	return (
		<>
			<SiteHeader breadcrumbs={[{ key: "episodes", label: "Episodes" }]} />
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<DataTable
							columns={columns}
							data={data?.data ?? []}
							isFetching={isFetching}
							handleReload={refetch}
							filters={filters}
							onFilterChange={(filters) => setFilters(filters)}
							paginationState={paginationState}
							onPaginationChange={(pagination) =>
								setFilters(
									typeof pagination === "function" ? pagination(paginationState) : pagination,
								)
							}
							sortingState={sortingState}
							onSortingChange={(updaterOrValue) => {
								const newSortingState =
									typeof updaterOrValue === "function"
										? updaterOrValue(sortingState)
										: updaterOrValue;
								return setFilters({ sortBy: stateToSortBy(newSortingState) });
							}}
							rowCount={data?.meta.totalItems}
							toolbar={<ToolBar />}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
